import { PaymentMethod, isPaymentMethodDataOneClick } from '@interfaces';
import { Fragment, useState } from 'react';
import {
  OptionDiv,
  TextDiv,
  SkeletonOptionTitleText,
  SkeletonOptionDescriptionText,
  OptionTitleText,
  OptionDescriptionText,
  CardIconContainer,
  ArrowIconContainer,
  NewCardLoaderContainer,
  OptionLeftContainer,
} from './PaymentsOptions.styles';
import { usePermissions } from '@hooks/usePermissions';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { useOneClickRedirect } from '@screens/payment/methods/hooks/useOneClickRedirect';
import { CardIcon } from '../../components/CardIcon';
import { useOrderAndPaymentInfo } from '../zustand/paymentMethodStore';
import {
  ErrorModal,
  ErrorModalTypes,
} from '@screens/checkout/paymentConfirm/components/ErrorModal';
import { CircularProgress, Divider } from '@mui/material';
import { PaymentMethodMainCard } from '../styles';

interface Props {
  loading: boolean;
  handleSelect: (e: unknown, paymentMethod: PaymentMethod) => void;
  quoteLoading?: boolean;
}

export function PaymentMethods({ loading, handleSelect, quoteLoading }: Props) {
  const { userCan } = usePermissions();
  const isAssistantUser = userCan('MANAGE_USER');

  const [error, setError] = useState(false);
  const [loadingOneClick, setLoadingOneClick] = useState(false);

  const handleShowError = () => {
    setError(true);
  };

  const [redirectToOneClick] = useOneClickRedirect(
    '/checkout/metodo-pago',
    handleShowError,
  );

  const { paymentMethods } = useOrderAndPaymentInfo();

  return (
    <>
      <PaymentMethodMainCard>
        {loading ? (
          <>
            <OptionDiv key={0}>
              <OptionLeftContainer>
                <CardIconContainer />
                <TextDiv>
                  <SkeletonOptionTitleText />
                  <SkeletonOptionDescriptionText />
                </TextDiv>
              </OptionLeftContainer>
              <ArrowIconContainer>
                <ArrowForwardIosRoundedIcon color="primary" />
              </ArrowIconContainer>
            </OptionDiv>
            <Divider />
          </>
        ) : (
          <>
            {paymentMethods?.map((paymentMethod) => {
              if (isPaymentMethodDataOneClick(paymentMethod?.data)) {
                const data = paymentMethod.data;
                const typeCapitalize =
                  paymentMethod.type === 'ONE_CLICK'
                    ? 'Webpay OneClick'
                    : (paymentMethod.type?.replace(/_/g, '') || '').replace(
                        /^\w/,
                        (c) => c.toUpperCase(),
                      );
                return (
                  <Fragment key={paymentMethod.id}>
                    <OptionDiv
                      quoteLoading={quoteLoading || loadingOneClick}
                      onClick={() => {
                        if (!loading && !quoteLoading) {
                          handleSelect(paymentMethod?.uuid, paymentMethod);
                        }
                      }}
                    >
                      <OptionLeftContainer>
                        <CardIcon creditCardType={data.creditCardType} />
                        <TextDiv>
                          <OptionTitleText>
                            {data.creditCardType}{' '}
                            {data.last4CardDigits.replace(
                              /XXXXXXXXXXXX/g,
                              '•••• ',
                            )}
                          </OptionTitleText>
                          <OptionDescriptionText light>
                            {`${typeCapitalize}`}
                          </OptionDescriptionText>
                        </TextDiv>
                      </OptionLeftContainer>
                      <ArrowIconContainer>
                        <ArrowForwardIosRoundedIcon color="primary" />
                      </ArrowIconContainer>
                    </OptionDiv>
                    <Divider />
                  </Fragment>
                );
              }
            })}
          </>
        )}
        {!isAssistantUser && (
          <label>
            <OptionDiv
              quoteLoading={quoteLoading}
              onClick={() => {
                setLoadingOneClick(true);
                redirectToOneClick();
              }}
            >
              <OptionLeftContainer>
                <CardIcon />
                <TextDiv>
                  <OptionTitleText>Nueva tarjeta</OptionTitleText>
                  <OptionDescriptionText light>
                    Crédito o débito
                  </OptionDescriptionText>
                </TextDiv>
              </OptionLeftContainer>
              {!loadingOneClick ? (
                <ArrowIconContainer>
                  <ArrowForwardIosRoundedIcon color="primary" />
                </ArrowIconContainer>
              ) : (
                <NewCardLoaderContainer>
                  <CircularProgress size={25} color="primary" />
                </NewCardLoaderContainer>
              )}
            </OptionDiv>
          </label>
        )}
      </PaymentMethodMainCard>
      {error && (
        <ErrorModal
          handleHideError={() => setError(false)}
          uuid=""
          errorType={ErrorModalTypes.REGISTRY}
        />
      )}
    </>
  );
}
